import { Component, Output, EventEmitter, OnInit, OnDestroy, HostListener, ElementRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { OrganizationActivityService } from '../../data-services/organization-activity.service';
import { CustomClaimService } from '../../services/custom-claim.service';
import { VersionCheckService } from '../../services/version-check.service';
import { User } from '../../data-models/user.model';

/**
 * Top Bar Navigation Component
 * Contains theme toggle, notifications, and user profile dropdown
 * Based on dashboard mockups showing white/dark top bar with user info
 */
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private activityService = inject(OrganizationActivityService);
  private customClaimService = inject(CustomClaimService);
  private versionCheckService = inject(VersionCheckService);
  readonly updateAvailable = this.versionCheckService.updateAvailable;

  currentTheme$!: any;
  showUserMenu: boolean = false;
  currentUser: User | null = null;

  // Role-based visibility
  isOwnerOrChiefSupervisor = signal(false);

  // Computed signal for unseen activity badge (red dot)
  readonly hasUnseenActivity = computed(() => this.activityService.hasUnseenActivities$());

  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.currentTheme$ = this.themeService.theme$;
  }

  ngOnInit(): void {
    this.authService.currentUser$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.currentUser = user;
      // Update role-based visibility when user changes
      this.isOwnerOrChiefSupervisor.set(this.customClaimService.isOwnerOrChiefSupervisor());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.showUserMenu) {
      this.showUserMenu = false;
    }
  }

  /**
   * Toggle theme between light and dark modes
   */
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /**
   * Toggle user dropdown menu
   */
  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  /**
   * Emit sidebar toggle event
   */
  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  /**
   * Get user initials for avatar
   */
  getUserInitials(): string {
    if (!this.currentUser?.displayName && !this.currentUser?.email) {
      return 'U';
    }

    const name = this.currentUser.displayName || this.currentUser.email || '';
    const names = name.split(' ');
    return names.map(n => n[0]).join('').slice(0, 2).toUpperCase();
  }

  /**
   * Get user display name (capitalized first and last name)
   */
  getUserName(): string {

    const displayName = this.currentUser?.displayName || this.currentUser?.email?.split('@')[0] || 'User';
    const names = displayName.split(' ');

    // Capitalize each name part
    const capitalizedNames = names.map(name =>
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
    );

    return capitalizedNames.join(' ');
  }

  /**
   * Get user email
   */
  getUserEmail(): string {
    return this.currentUser?.email || '';
  }

  /**
   * Navigate to profile
   */
  goToProfile(): void {
    this.router.navigate(['/profile']);
    this.showUserMenu = false;
  }

  /**
   * Navigate to company profile
   */
  goToCompanyProfile(): void {
    this.router.navigate(['/company-profile']);
    this.showUserMenu = false;
  }

  /**
   * Navigate to team members
   */
  goToTeam(): void {
    this.router.navigate(['/team']);
    this.showUserMenu = false;
  }

  /**
   * Navigate to activity page
   */
  goToActivity(): void {
    this.router.navigate(['/activity']);
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    this.showUserMenu = false;
    await this.authService.logout();
  }

  reloadForUpdate(): void {
    this.versionCheckService.reloadApp();
  }
}
